import React, { useEffect, useState } from "react"
import { bestDescribesOptions } from '../../forms/SignUpApplyForms/SignUpApplyValuesHelper'
import PropTypes from "prop-types"
import { 
  Grid, 
  Typography, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper,
  TablePagination,
  Box,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  CircularProgress
} from "@material-ui/core"
import { Search as SearchIcon } from "@material-ui/icons"
import { useHistory } from "react-router-dom"
import { paths } from "../../routes/paths"
import styles from "./AdminUserApplicationListContainer.module.scss"
import Loading from "../../components/Loading/Loading"

function AdminUserApplicationListContainer({ 
  applications = [], 
  loading, 
  error,
  currentPage = 1,
  totalPages = 1,
  totalCount = 0,
  onPageChange,
  filters = {},
  onFilterChange
}) {
  const history = useHistory()
  const [localFilters, setLocalFilters] = useState(filters)

  useEffect(() => {
    setLocalFilters(filters)
  }, [filters])

  const handleOpenApplication = (id) => {
    history.push(paths.adminUserApplication(id))
  }

  const handleFilterUpdate = (key, value) => {
    const newFilters = { ...localFilters, [key]: value }
    setLocalFilters(newFilters)
    if (onFilterChange) {
      onFilterChange(newFilters)
    }
  }

  const handleSearchChange = (event) => {
    const value = event.target.value
    setLocalFilters({ ...localFilters, search: value })
    // Debounce search - only trigger after user stops typing
    clearTimeout(window.searchTimeout)
    window.searchTimeout = setTimeout(() => {
      if (onFilterChange) {
        onFilterChange({ ...localFilters, search: value })
      }
    }, 500)
  }

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.content}>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
            <CircularProgress />
          </Box>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.content}>
          <Typography variant="h4" className={styles.title}>
            Error
          </Typography>
          <Typography variant="body1" className={styles.error}>
            {error}
          </Typography>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <Typography variant="h4" className={styles.title}>
          User Applications
        </Typography>
        <Typography variant="body1" className={styles.subtitle}>
          Total applications: {totalCount}
        </Typography>

        {/* Search and Filter Section */}
        <Box className={styles.filtersSection} mb={2}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12}>
              <TextField
                fullWidth
                placeholder="Search by name or email..."
                value={localFilters.search || ""}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                variant="outlined"
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth variant="outlined" size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={localFilters.status || ""}
                  onChange={(e) => handleFilterUpdate('status', e.target.value)}
                  label="Status"
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="under_review">Under Review</MenuItem>
                  <MenuItem value="request_details">Additional Documents Required</MenuItem>
                  <MenuItem value="request_details_applied">Request Details Applied</MenuItem>
                  <MenuItem value="approved">Approved</MenuItem>
                  <MenuItem value="rejected">Declined</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth variant="outlined" size="small">
                <InputLabel>Business Type</InputLabel>
                <Select
                  value={localFilters.business_type || ""}
                  onChange={(e) => handleFilterUpdate('business_type', e.target.value)}
                  label="Business Type"
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="company_seeker">Company Seeker</MenuItem>
                  <MenuItem value="company_provider">Company Provider</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth variant="outlined" size="small">
                <InputLabel>Business Category</InputLabel>
                <Select
                  value={localFilters.business_category || ""}
                  onChange={(e) => handleFilterUpdate('business_category', e.target.value)}
                  label="Business Category"
                >
                  <MenuItem value="">All</MenuItem>
                  {bestDescribesOptions.map(option => (
                    <MenuItem key={option.id} value={option.id}>{option.value}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth variant="outlined" size="small">
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={localFilters.sort_by || "created_at"}
                  onChange={(e) => handleFilterUpdate('sort_by', e.target.value)}
                  label="Sort By"
                >
                  <MenuItem value="created_at">Date Submitted</MenuItem>
                  <MenuItem value="first_name">First Name</MenuItem>
                  <MenuItem value="last_name">Last Name</MenuItem>
                  <MenuItem value="email">Email</MenuItem>
                  <MenuItem value="status">Status</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Box>

        <TableContainer component={Paper} className={styles.tableContainer}>
          <Table className={styles.table}>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Business Type</TableCell>
                <TableCell>Business Category</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Subscription</TableCell>
                <TableCell>Submitted</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {applications && applications.length > 0 ? (
                applications.map((application) => {
                  return (
                    <TableRow key={application.id}>
                      <TableCell>{`${application.first_name} ${application.last_name}`}</TableCell>
                      <TableCell>{application.email}</TableCell>
                      <TableCell>{application.business_type}</TableCell>
                      <TableCell>{
                        bestDescribesOptions.find(opt => opt.id === application.business_category)?.value || application.business_category
                      }</TableCell>
                      <TableCell>
                        <span className={`${styles.status} ${styles[application.status]}`}>
                          {application.status}
                        </span>
                      </TableCell>
                      <TableCell>
                        {application.status === 'approved' && application.subscription_status && (
                          <span className={`${styles.subscriptionStatus}`}>
                            {application.subscription_status}
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        {new Date(application.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={() => handleOpenApplication(application.id)}
                          className={styles.openButton}
                        >
                          Open
                        </Button>
                      </TableCell>
                    </TableRow>
                  )
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No applications found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={totalCount}
            page={currentPage - 1}
            onPageChange={onPageChange}
            rowsPerPage={20}
            rowsPerPageOptions={[20]}
            className={styles.pagination}
          />
        </TableContainer>
      </div>
    </div>
  )
}

AdminUserApplicationListContainer.propTypes = {
  applications: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      first_name: PropTypes.string.isRequired,
      last_name: PropTypes.string.isRequired,
      email: PropTypes.string.isRequired,
      business_type: PropTypes.string.isRequired,
      status: PropTypes.string.isRequired,
      subscription_status: PropTypes.string,
      created_at: PropTypes.string.isRequired,
    })
  ),
  loading: PropTypes.bool.isRequired,
  error: PropTypes.string,
  currentPage: PropTypes.number,
  totalPages: PropTypes.number,
  totalCount: PropTypes.number,
  onPageChange: PropTypes.func.isRequired,
}

AdminUserApplicationListContainer.defaultProps = {
  applications: [],
  currentPage: 1,
  totalPages: 1,
  totalCount: 0,
  error: null,
}

export default AdminUserApplicationListContainer